import { computed, ref, watchEffect } from "vue";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "smake.theme";

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

const preference = ref<ThemePreference>(typeof window === "undefined" ? "system" : readPreference());
const systemDark = ref(typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);

if (typeof window !== "undefined" && window.matchMedia) {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
    systemDark.value = e.matches;
  });
}

const isDark = computed(() => preference.value === "dark" || (preference.value === "system" && systemDark.value));

let applied = false;
function applyOnce() {
  if (applied || typeof document === "undefined") return;
  applied = true;
  watchEffect(() => {
    document.documentElement.classList.toggle("dark", isDark.value);
  });
}

/** Light / dark / system theme, remembered per browser. */
export function useTheme() {
  applyOnce();
  function setTheme(value: ThemePreference) {
    preference.value = value;
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* private mode: keep for this tab only */
    }
  }
  return { preference, isDark, setTheme };
}
